export const ACCENT = "#10B981";
export const C = { cyan: "#2DD4BF", purple: "#3B82F6", blue: "#2563EB", red: "#F43F5E", yellow: "#F59E0B", green: "#10B981", grey: "#64748b" };
export const SITE_COLORS = { Qatar: C.cyan, India: C.yellow, Unknown: C.grey };

export const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
export const median = (a) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};

export const fmtN = (n, d = 1) => (n == null || isNaN(n) ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
export const fmtPct = (n) => (n == null || isNaN(n) ? "—" : `${Number(n).toFixed(1)}%`);
export const fmtInt = (n) => (n == null || isNaN(n) ? "—" : Math.round(n).toLocaleString());

export const METRICS = [
  { k: "voc", label: "VOC Top-2-Box", pct: true, target: 70 },
  { k: "surveys", label: "Surveys", int: true },
  { k: "pmt", label: "PMT Score", pct: true, target: 80 },
  { k: "crit", label: "PMT Criticals", int: true, lower: true },
  { k: "qsl", label: "QSL Score", pct: true, target: 80 },
  { k: "ahtWA", label: "AHT WhatsApp (sec)", lower: true, d: 0 },
  { k: "ahtLC", label: "AHT Live Chat (sec)", lower: true, d: 0 },
  { k: "frtWA", label: "FRT WhatsApp (min)", lower: true, d: 2 },
  { k: "frtLC", label: "FRT Live Chat (min)", lower: true, d: 2 },
];

export const siteOf = (data, c) => data.agents[c]?.site || "Unknown";

export function filterData(data, { from, to, site }) {
  const okSite = (c) => site === "All" || siteOf(data, c) === site;
  const inR = (d) => d && d >= from && d <= to;
  return {
    voc: data.voc.filter((r) => inR(r.d) && okSite(r.c)),
    chat: data.chat.filter((r) => inR(r.d) && okSite(r.c)),
    pmt: data.pmt.filter((r) => inR(r.d) && okSite(r.c)),
    qsl: data.qsl.filter((r) => inR(r.d) && okSite(r.c)),
  };
}

export function kpisOf(sl) {
  const n = sl.voc.reduce((a, r) => a + r.n, 0);
  const t2 = sl.voc.reduce((a, r) => a + r.t2b, 0);
  const cW = sl.chat.filter((r) => r.ch === "WA");
  const cL = sl.chat.filter((r) => r.ch === "LC");
  const ps = sl.pmt.map((p) => p.score).filter((v) => v != null);
  const qs = sl.qsl.map((q) => q.score).filter((v) => v != null);
  return {
    voc: n ? (100 * t2) / n : null, surveys: n,
    pmt: mean(ps), evals: sl.pmt.length, crit: sl.pmt.filter((p) => p.rating === "Critical").length,
    qsl: qs.length ? mean(qs) : null, audits: sl.qsl.length, fatals: sl.qsl.filter((q) => q.assessment === "Fatal").length,
    ahtWA: mean(cW.map((r) => r.aht).filter((v) => v != null)), ahtLC: mean(cL.map((r) => r.aht).filter((v) => v != null)),
    frtWA: mean(cW.map((r) => r.frt).filter((v) => v != null)), frtLC: mean(cL.map((r) => r.frt).filter((v) => v != null)),
  };
}

export function dailySeries(rows, fn) {
  const by = {};
  rows.forEach((r) => { (by[r.d] = by[r.d] || []).push(r); });
  return Object.keys(by).sort().map((d) => ({ d, v: fn(by[d]) })).filter((p) => p.v != null);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function periodOf(d, mode) {
  const [y, m, day] = d.split("-").map(Number);
  if (mode === "m") return { key: d.slice(0, 7), label: `${MONTHS[m - 1]} ${String(y).slice(2)}` };
  const w = day <= 7 ? 1 : day <= 14 ? 2 : day <= 21 ? 3 : day <= 28 ? 4 : 5;
  return { key: `${d.slice(0, 7)}-W${w}`, label: `${MONTHS[m - 1]} W${w}` };
}

export function trendSeries(data, { mode, ciscos, site }) {
  const okC = ciscos ? (c) => ciscos.has(c) : site && site !== "All" ? (c) => siteOf(data, c) === site : () => true;
  const buckets = {};
  const push = (feed, r) => {
    if (!r.d || !okC(r.c)) return;
    const p = periodOf(r.d, mode);
    const b = (buckets[p.key] = buckets[p.key] || { key: p.key, label: p.label, voc: [], chat: [], pmt: [], qsl: [] });
    b[feed].push(r);
  };
  data.voc.forEach((r) => push("voc", r));
  data.chat.forEach((r) => push("chat", r));
  data.pmt.forEach((r) => push("pmt", r));
  data.qsl.forEach((r) => push("qsl", r));
  return Object.values(buckets).sort((a, b) => (a.key < b.key ? -1 : 1)).map((b) => ({ key: b.key, label: b.label, ...kpisOf(b) }));
}

export function computeAgentRows(data, sl) {
  const by = {};
  const g = (c) => (by[c] = by[c] || { voc: [], chat: [], pmt: [], qsl: [] });
  sl.voc.forEach((r) => g(r.c).voc.push(r));
  sl.chat.forEach((r) => g(r.c).chat.push(r));
  sl.pmt.forEach((r) => g(r.c).pmt.push(r));
  sl.qsl.forEach((r) => g(r.c).qsl.push(r));
  return Object.keys(data.agents).map((c) => {
    const a = data.agents[c];
    const k = kpisOf(by[c] || { voc: [], chat: [], pmt: [], qsl: [] });
    const ad = data.adherence[c];
    const parts = [k.voc, k.pmt, k.qsl, ad?.adh].filter((v) => v != null);
    return {
      c, name: a.name || `Agent ${c}`, tl: a.tl || "—", site: a.site, lob: a.lob || "—",
      ...k, adh: ad?.adh ?? null, critAvg: data.critical[c] ?? null,
      composite: parts.length ? mean(parts) : null,
    };
  });
}

export function computeTlRows(data, sl) {
  const tls = {};
  Object.entries(data.agents).forEach(([c, a]) => {
    const tl = a.tl && a.tl !== "-" ? a.tl : null;
    if (tl) (tls[tl] = tls[tl] || new Set()).add(c);
  });
  return Object.entries(tls).map(([tl, ciscos]) => {
    const team = {
      voc: sl.voc.filter((r) => ciscos.has(r.c)), chat: sl.chat.filter((r) => ciscos.has(r.c)),
      pmt: sl.pmt.filter((r) => ciscos.has(r.c)), qsl: sl.qsl.filter((r) => ciscos.has(r.c)),
    };
    const k = kpisOf(team);
    return { tl, agents: ciscos.size, ciscos, ...k, critPct: k.evals ? (100 * k.crit) / k.evals : null };
  });
}

export function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "string") return dir * av.localeCompare(bv);
    return dir * (av - bv);
  });
}

export function presetsFor(data) {
  const dates = Object.values(data.coverage).filter(Boolean);
  const latest = dates.length ? dates.sort().slice(-1)[0] : new Date().toISOString().slice(0, 10);
  const m = latest.slice(0, 7);
  return {
    latest, month: m,
    list: [
      { id: "w1", label: "W1", from: `${m}-01`, to: `${m}-07` },
      { id: "w2", label: "W2", from: `${m}-08`, to: `${m}-14` },
      { id: "w3", label: "W3", from: `${m}-15`, to: `${m}-21` },
      { id: "w4", label: "W4", from: `${m}-22`, to: `${m}-28` },
      { id: "mtd", label: "MTD", from: `${m}-01`, to: latest },
      { id: "all", label: "All", from: "2000-01-01", to: "2099-12-31" },
    ],
  };
}

export const covLabel = (d) => {
  if (!d) return "no data";
  const [, m, day] = d.split("-").map(Number);
  return `${MONTHS[m - 1]} ${day}`;
};
